import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Wallet, Save, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}`
});

const AffiliateWalletManagement = () => {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resellers, setResellers] = useState([]);
  const [filter, setFilter] = useState('');
  const [customEdit, setCustomEdit] = useState({}); // { [reseller_id]: percent or '' }
  const [txList, setTxList] = useState([]);
  const [txFilter, setTxFilter] = useState({ type: '', kako_id: '' });

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [cfg, res, tx] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reseller/admin/wallet-config`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/reseller/`, { headers: authHeaders() }),
        axios.get(`${BACKEND_URL}/api/reseller/admin/wallet-transactions`, { headers: authHeaders() })
      ]);
      setConfig(cfg.data);
      setResellers(res.data || []);
      setTxList(tx.data || []);
    } catch (err) {
      console.error('Erro ao carregar carteira admin:', err);
    }
  };

  const loadTx = async () => {
    try {
      const params = {};
      if (txFilter.type) params.tx_type = txFilter.type;
      if (txFilter.kako_id) params.kako_id = txFilter.kako_id.trim();
      const tx = await axios.get(`${BACKEND_URL}/api/reseller/admin/wallet-transactions`, {
        headers: authHeaders(),
        params
      });
      setTxList(tx.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    setSaving(true);
    try {
      const res = await axios.put(
        `${BACKEND_URL}/api/reseller/admin/wallet-config`,
        {
          global_commission_percent: parseFloat(config.global_commission_percent),
          commissions_enabled: !!config.commissions_enabled,
          redemptions_enabled: !!config.redemptions_enabled,
          min_redemption_coins: parseInt(config.min_redemption_coins, 10),
          max_daily_redemption_coins: parseInt(config.max_daily_redemption_coins, 10) || 0,
          exchange_enabled: !!config.exchange_enabled,
          min_exchange_coins: parseInt(config.min_exchange_coins, 10) || 100
        },
        { headers: authHeaders() }
      );
      setConfig(res.data);
      alert('✅ Configuração salva!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const saveCustom = async (reseller) => {
    const raw = customEdit[reseller.id];
    const percent = raw === '' || raw === undefined || raw === null ? null : parseFloat(raw);
    if (percent !== null && (isNaN(percent) || percent < 0 || percent > 100)) {
      alert('Percentual inválido (0 a 100)');
      return;
    }
    try {
      await axios.put(
        `${BACKEND_URL}/api/reseller/admin/${reseller.id}/custom-commission`,
        { percent },
        { headers: authHeaders() }
      );
      alert(percent === null
        ? `✅ ${reseller.nickname} agora usa o % global`
        : `✅ ${reseller.nickname} agora tem ${percent}% customizado`
      );
      loadAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const filteredResellers = resellers.filter((r) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.kako_id || '').toString().toLowerCase().includes(q) ||
      (r.nickname || '').toLowerCase().includes(q)
    );
  });

  if (!config) return <div className="p-4 text-gray-500">Carregando carteira de afiliados...</div>;

  return (
    <div className="space-y-6">
      {/* Configuração Global */}
      <Card data-testid="wallet-config-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-emerald-600" /> Configuração da Carteira de Afiliados
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>% Global de comissão</Label>
              <Input
                data-testid="wallet-global-percent"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={config.global_commission_percent}
                onChange={(e) => setConfig({ ...config, global_commission_percent: e.target.value })}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Aplicado a todas as vendas NOVAS (não recalcula histórico).
              </p>
            </div>
            <div>
              <Label>Resgate mínimo (moedas)</Label>
              <Input
                data-testid="wallet-min-redemption"
                type="number"
                min="1"
                value={config.min_redemption_coins}
                onChange={(e) => setConfig({ ...config, min_redemption_coins: e.target.value })}
              />
            </div>
            <div>
              <Label>Resgate máximo no dia (moedas)</Label>
              <Input
                data-testid="wallet-max-daily-redemption"
                type="number"
                min="0"
                value={config.max_daily_redemption_coins ?? 0}
                onChange={(e) => setConfig({ ...config, max_daily_redemption_coins: e.target.value })}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Ao atingir o limite, o afiliado só consegue resgatar novamente no dia seguinte. Use 0 para sem limite.
              </p>
            </div>
            <div>
              <Label>Intercâmbio mínimo (moedas amarelas)</Label>
              <Input
                data-testid="wallet-min-exchange"
                type="number"
                min="10"
                step="10"
                value={config.min_exchange_coins ?? 100}
                onChange={(e) => setConfig({ ...config, min_exchange_coins: e.target.value })}
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Troca de amarelas por Azulzinhas com dedução de 10% (só múltiplos de 10). 100% do valor vai para os prêmios da Roleta (60% Jackpot / 40% 2X).
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex items-center gap-3">
              <Switch
                data-testid="wallet-toggle-commissions"
                checked={!!config.commissions_enabled}
                onCheckedChange={(v) => setConfig({ ...config, commissions_enabled: v })}
              />
              <div>
                <p className="font-semibold text-sm">Comissões habilitadas</p>
                <p className="text-[11px] text-gray-500">Se desligado, nenhum afiliado ganha comissão em novas vendas.</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                data-testid="wallet-toggle-redemptions"
                checked={!!config.redemptions_enabled}
                onCheckedChange={(v) => setConfig({ ...config, redemptions_enabled: v })}
              />
              <div>
                <p className="font-semibold text-sm">Resgates habilitados</p>
                <p className="text-[11px] text-gray-500">Se desligado, afiliados não conseguem sacar saldo (saldo continua acumulando).</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                data-testid="wallet-toggle-exchange"
                checked={!!config.exchange_enabled}
                onCheckedChange={(v) => setConfig({ ...config, exchange_enabled: v })}
              />
              <div>
                <p className="font-semibold text-sm">Intercâmbio habilitado</p>
                <p className="text-[11px] text-gray-500">Troca de amarelas → Azulzinhas (dedução de 10%). Requer evento da Roleta ativo.</p>
              </div>
            </div>
          </div>

          <Button
            data-testid="wallet-save-config-btn"
            onClick={saveConfig}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvando...' : 'Salvar configuração'}
          </Button>
        </CardContent>
      </Card>

      {/* Comissões personalizadas */}
      <Card>
        <CardHeader>
          <CardTitle>Comissão personalizada por afiliado</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-3">
            <Input
              data-testid="wallet-reseller-filter"
              placeholder="Filtrar por KAKO ID ou nickname..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="max-w-md"
            />
          </div>

          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="text-left p-2 font-semibold">Afiliado</th>
                  <th className="text-left p-2 font-semibold">KAKO ID</th>
                  <th className="text-right p-2 font-semibold">Saldo</th>
                  <th className="text-right p-2 font-semibold">% aplicado</th>
                  <th className="text-right p-2 font-semibold">Novo %</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredResellers.map((r) => {
                  const currentCustom = r.custom_commission_percent;
                  const editValue =
                    customEdit[r.id] !== undefined
                      ? customEdit[r.id]
                      : currentCustom === null || currentCustom === undefined
                      ? ''
                      : currentCustom;
                  const effective = currentCustom !== null && currentCustom !== undefined
                    ? currentCustom
                    : config.global_commission_percent;
                  return (
                    <tr key={r.id} className="border-t hover:bg-purple-50/40">
                      <td className="p-2 font-medium">{r.nickname || '-'}</td>
                      <td className="p-2 font-mono text-xs">{r.kako_id}</td>
                      <td className="p-2 text-right font-semibold text-emerald-600">
                        {(r.wallet_balance || 0).toLocaleString()} 💰
                      </td>
                      <td className="p-2 text-right">
                        <span className={currentCustom !== null && currentCustom !== undefined ? 'font-semibold text-purple-700' : 'text-gray-700'}>
                          {effective}%
                        </span>
                        {currentCustom !== null && currentCustom !== undefined && (
                          <span className="ml-1 text-[10px] bg-purple-100 text-purple-700 px-1 py-0.5 rounded">custom</span>
                        )}
                      </td>
                      <td className="p-2 text-right">
                        <Input
                          data-testid={`wallet-custom-input-${r.id}`}
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="global"
                          value={editValue}
                          onChange={(e) => setCustomEdit({ ...customEdit, [r.id]: e.target.value })}
                          className="w-24 h-8 text-xs ml-auto"
                        />
                      </td>
                      <td className="p-2 text-right">
                        <Button
                          data-testid={`wallet-custom-save-${r.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => saveCustom(r)}
                        >
                          Aplicar
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filteredResellers.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-4 text-center text-gray-500">Nenhum afiliado encontrado.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Histórico de Transações */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Histórico de Movimentações da Carteira</span>
            <Button size="sm" variant="outline" onClick={loadTx}>
              <RefreshCw className="w-3 h-3 mr-1" /> Atualizar
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-3 flex-wrap">
            <select
              data-testid="wallet-tx-type-filter"
              value={txFilter.type}
              onChange={(e) => setTxFilter({ ...txFilter, type: e.target.value })}
              className="border rounded px-2 py-1 text-sm"
            >
              <option value="">Todos os tipos</option>
              <option value="credit">Créditos</option>
              <option value="redemption">Resgates</option>
              <option value="exchange">Intercâmbios</option>
            </select>
            <Input
              data-testid="wallet-tx-kako-filter"
              placeholder="Filtrar por KAKO ID do afiliado"
              value={txFilter.kako_id}
              onChange={(e) => setTxFilter({ ...txFilter, kako_id: e.target.value })}
              onKeyDown={(e) => { if (e.key === 'Enter') loadTx(); }}
              className="max-w-xs h-8 text-sm"
            />
            <Button size="sm" onClick={loadTx}>Aplicar filtros</Button>
          </div>

          {txList.length === 0 ? (
            <p className="text-center text-gray-500 text-sm py-6">Sem movimentações ainda.</p>
          ) : (
            <div className="overflow-x-auto max-h-[560px] overflow-y-auto border rounded-md scroll-smooth" data-testid="admin-wallet-tx-scroll">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="text-left p-2 font-semibold">Data</th>
                    <th className="text-left p-2 font-semibold">Afiliado</th>
                    <th className="text-left p-2 font-semibold">Tipo</th>
                    <th className="text-right p-2 font-semibold">Moedas</th>
                    <th className="text-right p-2 font-semibold">Saldo</th>
                    <th className="text-left p-2 font-semibold">Detalhe</th>
                  </tr>
                </thead>
                <tbody>
                  {txList.map((tx) => (
                    <tr key={tx.id} className="border-t">
                      <td className="p-2 whitespace-nowrap">{new Date(tx.created_at).toLocaleString('pt-BR')}</td>
                      <td className="p-2">
                        <div className="font-medium">{tx.reseller_nickname || '-'}</div>
                        <div className="text-[10px] font-mono text-gray-500">{tx.reseller_kako_id}</div>
                      </td>
                      <td className="p-2">
                        {tx.type === 'credit' ? (
                          <span className="text-[11px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded font-semibold">Crédito</span>
                        ) : tx.type === 'exchange' ? (
                          <span className="text-[11px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-semibold">Intercâmbio</span>
                        ) : (
                          <span className="text-[11px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold">Resgate</span>
                        )}
                        {tx.status === 'failed' && (
                          <span className="ml-1 text-[10px] bg-red-100 text-red-700 px-1 py-0.5 rounded">falhou</span>
                        )}
                      </td>
                      <td className={`p-2 text-right font-bold ${tx.type === 'credit' ? 'text-emerald-600' : tx.type === 'exchange' ? 'text-sky-600' : 'text-orange-600'}`}>
                        {tx.type === 'credit' ? '+' : '−'}{tx.coins.toLocaleString()}
                      </td>
                      <td className="p-2 text-right text-gray-600">{tx.balance_after?.toLocaleString?.() ?? '-'}</td>
                      <td className="p-2 text-[11px] text-gray-600">
                        {tx.type === 'credit' ? (
                          <>Venda {(tx.sale_coins || 0).toLocaleString()} × {tx.commission_percent}% {tx.order_id && <span className="font-mono text-gray-400">#{tx.order_id.slice(0, 8)}</span>}</>
                        ) : tx.type === 'exchange' ? (
                          <>Trocou por {(tx.blue_coins || 0).toLocaleString()} Azulzinhas 💙 (100% p/ fundo da Roleta)</>
                        ) : (
                          <>Enviado p/ KAKO {tx.kako_target_id}</>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AffiliateWalletManagement;
